import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Package, CheckCircle, Warning, Lock, Plus, Trash, Truck,
    CircleNotch, WarningCircle,
} from "@phosphor-icons/react";
import PlanSectionAccordion from "@/components/case/PlanSectionAccordion";


/* ============================================================================
   ManufacturingSpecPanel — set/edit the spec that tells the production team
   WHICH aligner sheet + retainer sheet to use on this case.

   Used in two places:
     - Planner case workspace (Plan tab) — initial spec authoring
     - QA case workspace — confirm/override during QA review

   The spec locks when the case enters production (status=in_production,
   shipped, in_treatment, completed). After that this panel renders read-only.

   iter97.43 — Killed the explicit "Set spec → Edit → Save" click-dance.
   Form is ALWAYS inline-editable when the case isn't locked; every field
   debounce-auto-saves like the plan editor. The panel also self-wraps in
   `PlanSectionAccordion` (section 07) so it joins the planner's accordion
   flow on the Author the plan tab.
   ========================================================================== */

const ARCH_OPTIONS = [
    { id: "both", label: "Upper + Lower" },
    { id: "upper", label: "Upper only" },
    { id: "lower", label: "Lower only" },
];

const DEBOUNCE_MS = 600;
const SPEC_ANCHOR_ID = "sec-manufacturing";
const LOCKED_STATUSES = new Set(["in_production", "shipped", "in_treatment", "treatment_started", "completed"]);

export function ManufacturingSpecPanel({ caseId, status, allowEdit = true, plan, sectionNumber = 7 }) {
    const [data, setData] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [form, setForm] = useState({
        aligner_sheet_material_id: "",
        retainer_sheet_material_id: null,
        aligner_stages_count: 0,
        arches: "both",
        sheets_per_stage: 1,
        retainer_count: 2,
        notes: "",
        phases: [],
    });
    const formRef = useRef(form);
    const seededRef = useRef(false);
    const debounceRef = useRef(null);
    const [saveState, setSaveState] = useState({ status: "idle", at: null, error: null });
    /* Planner explicitly acknowledges a tier overage before the inline
       auto-save will commit the overage flag. Resets after the spec is
       reloaded. */
    const [tierOverageAck, setTierOverageAck] = useState(false);
    /* iter97.43 — accordion state. Self-managed; opens via the rail's
       `plan-section-open` event for `sec-manufacturing`. */
    const [isOpen, setIsOpen] = useState(false);

    const load = useCallback(() => {
        api.get(`/cases/${caseId}/manufacturing`).then((r) => setData(r.data)).catch(() => setData(null));
        api.get("/admin/inventory/materials").then((r) => setMaterials((r.data || []).filter((m) => m.is_active !== false))).catch(() => setMaterials([]));
    }, [caseId]);

    /* iter97.41 — re-fetch when the case plan's aligner counts (or wear
       cadence) change. Without these deps the derived spec stayed stale
       after the planner edited "Aligners & Wear Schedule". */
    useEffect(() => { load(); }, [
        caseId,
        plan?.aligner_count_upper,
        plan?.aligner_count_lower,
        plan?.wear_schedule_default_days,
        load,
    ]);

    const aligner_options = useMemo(() => materials.filter((m) => m.category === "tpu_sheet"), [materials]);

    const locked = LOCKED_STATUSES.has(status);

    /* iter97.35 — pull planner-authored Upper/Lower aligner counts from the
       case plan (surfaced via GET /cases/{id}/manufacturing.derived) so we
       can auto-fill Stages, Arches, Sheets/stage. */
    const derived = data?.derived || { aligner_stages_count: 0, arches: "", sheets_per_stage: 0, total_sheets: 0, aligner_count_upper: 0, aligner_count_lower: 0, ready: false };
    const tierLimit = data?.tier_limit || null;
    const stagesOverflow = tierLimit && derived.aligner_stages_count > tierLimit.included_aligners;
    const overageCount = stagesOverflow ? (derived.aligner_stages_count - tierLimit.included_aligners) : 0;
    const overageAmount = stagesOverflow ? overageCount * (tierLimit.overage_per_aligner || 0) : 0;

    /* Seed form once when the spec arrives. We deliberately do NOT re-seed
       on subsequent loads (plan changes trigger reload but we don't want
       to clobber the planner's mid-typing edits). Pre-existing spec rows
       are unwound into the form; missing fields default to sensible values. */
    useEffect(() => {
        if (seededRef.current || !data) return;
        const s = data.spec || {};
        const seed = {
            aligner_sheet_material_id: s.aligner_sheet_material_id || "",
            retainer_sheet_material_id: null, // Invisique™ Infinite, always
            aligner_stages_count: derived.aligner_stages_count || 0,
            arches: derived.arches || "both",
            sheets_per_stage: derived.sheets_per_stage || 1,
            retainer_count: s.retainer_count ?? 2,
            notes: s.notes || "",
            phases: Array.isArray(s.phases) ? s.phases.map((p) => ({ ...p })) : [],
        };
        formRef.current = seed;
        setForm(seed);
        seededRef.current = true;
    }, [data, derived.aligner_stages_count, derived.arches, derived.sheets_per_stage]);

    /* When the plan changes after seeding (planner bumped Upper aligners),
       sync the derived form fields without touching user-authored fields. */
    useEffect(() => {
        if (!seededRef.current) return;
        setForm((prev) => {
            const next = {
                ...prev,
                aligner_stages_count: derived.aligner_stages_count || 0,
                arches: derived.arches || prev.arches || "both",
                sheets_per_stage: derived.sheets_per_stage || 1,
            };
            formRef.current = next;
            return next;
        });
        setTierOverageAck(false);
    }, [derived.aligner_stages_count, derived.arches, derived.sheets_per_stage]);

    /* Listen for rail click → open self if event targets sec-manufacturing.
       iter97.44 — also honour the Expand all / Collapse all bulk toggle
       dispatched by PlanEditorInline so the planner can flip every section
       open/closed in one click. */
    useEffect(() => {
        if (locked || !allowEdit) return undefined;
        const openHandler = (e) => {
            if (e?.detail?.sectionId === SPEC_ANCHOR_ID) setIsOpen(true);
        };
        const bulkHandler = (e) => {
            const open = !!e?.detail?.open;
            setIsOpen(open);
        };
        window.addEventListener("plan-section-open", openHandler);
        window.addEventListener("plan-accordion-bulk-toggle", bulkHandler);
        return () => {
            window.removeEventListener("plan-section-open", openHandler);
            window.removeEventListener("plan-accordion-bulk-toggle", bulkHandler);
        };
    }, [locked, allowEdit]);

    const validatePhases = (snapshot) => {
        const totalStages = Number(snapshot.aligner_stages_count) || 0;
        const phases = (snapshot.phases || []).filter((p) => p.stage_from && p.stage_to);
        if (phases.length === 0) return { ok: true, phases: [] };
        const sorted = [...phases].sort((a, b) => Number(a.stage_from) - Number(b.stage_from));
        if (Number(sorted[0].stage_from) !== 1) return { ok: false, error: "First phase must start at stage 1" };
        if (totalStages > 0 && Number(sorted[sorted.length - 1].stage_to) !== totalStages) {
            return { ok: false, error: `Last phase must end at stage ${totalStages}` };
        }
        for (let i = 0; i < sorted.length; i++) {
            if (Number(sorted[i].stage_to) < Number(sorted[i].stage_from)) {
                return { ok: false, error: `Phase ${i + 1} has end stage before start stage` };
            }
            if (i > 0 && Number(sorted[i].stage_from) !== Number(sorted[i - 1].stage_to) + 1) {
                return { ok: false, error: "Phases must be contiguous — no gaps or overlaps" };
            }
        }
        return { ok: true, phases: sorted.map((p) => ({ stage_from: Number(p.stage_from), stage_to: Number(p.stage_to) })) };
    };

    const saveNow = useCallback(async (snapshot) => {
        // Empty drafts (no aligner sheet picked yet) silently skip — there's
        // nothing meaningful to persist and the empty state UX is the planner
        // hint to pick one.
        if (!snapshot.aligner_sheet_material_id) return;
        // Derived stages must be > 0 — otherwise plan isn't ready and the
        // backend would reject the payload. Surface a soft hint instead of
        // raising a toast on every keystroke.
        if ((Number(snapshot.aligner_stages_count) || 0) <= 0) {
            setSaveState({ status: "error", at: null, error: "Set Upper / Lower aligners in the Wear Schedule first." });
            return;
        }
        const { ok, error, phases } = validatePhases(snapshot);
        if (!ok) {
            setSaveState({ status: "error", at: null, error });
            return;
        }
        setSaveState((s) => ({ ...s, status: "saving" }));
        try {
            const payload = {
                aligner_sheet_material_id: snapshot.aligner_sheet_material_id,
                retainer_sheet_material_id: null,
                aligner_stages_count: Number(snapshot.aligner_stages_count) || 0,
                arches: snapshot.arches || "both",
                sheets_per_stage: Number(snapshot.sheets_per_stage) || 1,
                retainer_count: Number(snapshot.retainer_count) || 0,
                notes: snapshot.notes || "",
                phases,
                tier_overage_ack: stagesOverflow ? {
                    acknowledged: tierOverageAck,
                    tier_id: tierLimit?.tier_id,
                    tier_name: tierLimit?.tier_name,
                    included: tierLimit?.included_aligners,
                    planned: derived.aligner_stages_count,
                    overage: overageCount,
                    overage_amount: overageAmount,
                    acknowledged_at: tierOverageAck ? new Date().toISOString() : null,
                } : null,
            };
            const r = await api.put(`/cases/${caseId}/manufacturing-spec`, payload);
            setData(r.data);
            setSaveState({ status: "saved", at: new Date(), error: null });
        } catch (e) {
            const detail = e.response?.data?.detail || "Couldn't save spec";
            setSaveState({ status: "error", at: null, error: detail });
            toast.error(detail);
        }
    }, [caseId, stagesOverflow, tierOverageAck, tierLimit, derived.aligner_stages_count, overageCount, overageAmount]);

    const scheduleSave = useCallback((next) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => saveNow(next), DEBOUNCE_MS);
    }, [saveNow]);

    const upd = (k, v) => {
        const next = { ...formRef.current, [k]: v };
        formRef.current = next;
        setForm(next);
        scheduleSave(next);
    };

    const updEager = (k, v) => {
        const next = { ...formRef.current, [k]: v };
        formRef.current = next;
        setForm(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        saveNow(next);
    };

    /* When the planner ticks/unticks the tier-overage ack, immediately
       re-save the spec so the flag is persisted without waiting for
       another field edit. */
    useEffect(() => {
        if (!seededRef.current || !form.aligner_sheet_material_id) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        saveNow(formRef.current);
    }, [tierOverageAck]);

    useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

    if (!data) {
        return (
            <div id={SPEC_ANCHOR_ID} className="rounded-2xl border border-border bg-card p-5 text-muted-foreground text-xs scroll-mt-24" data-testid="manufacturing-spec-panel">
                Loading manufacturing spec…
            </div>
        );
    }

    const spec = data.spec || {};
    const hasSpec = !!spec.aligner_sheet_material_id;
    const totalSheets = (spec.aligner_stages_count || 0) * (spec.sheets_per_stage || 1);

    /* Build a one-line summary for the accordion collapsed state. */
    const buildSummary = () => {
        if (locked) return "Locked — production has started";
        if (hasSpec) {
            const m = spec.aligner_sheet_material;
            const sheets = totalSheets;
            const parts = [];
            if (m) parts.push(m.name);
            if (sheets > 0) parts.push(`${sheets} sheet${sheets === 1 ? "" : "s"}`);
            if (spec.retainer_count) parts.push(`${spec.retainer_count} retainer${spec.retainer_count === 1 ? "" : "s"}`);
            return parts.join(" · ");
        }
        if (derived.ready) {
            const needed = (derived.aligner_stages_count || 0) * (derived.sheets_per_stage || 1);
            return `Pick aligner sheet · plan needs ${needed} sheets`;
        }
        return "Not authored";
    };

    /* Locked case: render the read-only SpecDisplay (no accordion). The
       case is already in production, so any UI to mutate would be wrong. */
    if (locked) {
        return (
            <div id={SPEC_ANCHOR_ID} className="rounded-2xl border border-border bg-card p-5 scroll-mt-24" data-testid="manufacturing-spec-panel">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground flex items-center gap-1.5">
                            <Package size={12} weight="fill" className="text-primary" /> Manufacturing spec
                            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                <Lock size={9} /> Locked
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Production has started — the spec is now frozen for accountability.
                        </div>
                    </div>
                </div>
                <SpecDisplay spec={spec} derived={derived} locked totalSheets={totalSheets} />
            </div>
        );
    }

    /* Read-only viewer (CoE viewer / no edit permission): show SpecDisplay
       in its own card without the accordion chrome. */
    if (!allowEdit) {
        return (
            <div id={SPEC_ANCHOR_ID} className="rounded-2xl border border-border bg-card p-5 scroll-mt-24" data-testid="manufacturing-spec-panel">
                <SpecDisplay spec={spec} derived={derived} locked={false} totalSheets={totalSheets} />
            </div>
        );
    }

    /* Active authoring path — accordion wrapper + inline auto-save form. */
    const isComplete = !!form.aligner_sheet_material_id && (stagesOverflow ? tierOverageAck : true);
    const summary = buildSummary();

    return (
        <div data-testid="manufacturing-spec-panel">
            <PlanSectionAccordion
                anchorId={SPEC_ANCHOR_ID}
                number={sectionNumber}
                label="Manufacturing spec"
                summary={summary}
                isOpen={isOpen}
                isComplete={isComplete}
                onToggle={() => setIsOpen((v) => !v)}
                onNext={null}
                hideNext
                headerRight={<SpecSaveIndicator state={saveState} />}
                /* iter98.99n — Manufacturing spec is the lab/fab
                   handoff stage, so it shares the rose accent with
                   the Aligners & wear schedule + Retention plan
                   sections above it. Visually groups all "this goes
                   to the lab" sections together. */
                accent="rose"
            >
                <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">
                    Pick the TPU sheet for fabrication. Stages, arches, and sheets-per-stage are calculated live from the Aligners &amp; Wear Schedule above. Every field auto-saves.
                </p>
                <div className="space-y-3" data-testid="mfg-spec-form">
                    <div>
                        <Label>Aligner sheet (required)</Label>
                        <select
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={form.aligner_sheet_material_id || ""}
                            onChange={(e) => updEager("aligner_sheet_material_id", e.target.value)}
                            data-testid="mfg-spec-aligner"
                        >
                            <option value="">Select TPU sheet…</option>
                            {aligner_options.map((m) => <option key={m.id} value={m.id}>{m.sku} — {m.name}</option>)}
                        </select>
                    </div>
                    {/* Retainer sheet is always Invisique™ Infinite — static pill, no decision. */}
                    <RetainerStaticPill />

                    <div className="grid grid-cols-4 gap-3">
                        <ReadOnlyField
                            label="Stages"
                            value={derived.aligner_stages_count || "—"}
                            hint={derived.ready ? `from plan: ${derived.aligner_count_upper}U / ${derived.aligner_count_lower}L` : "set Upper/Lower in Wear Schedule"}
                            testid="mfg-spec-stages"
                        />
                        <ReadOnlyField
                            label="Arches"
                            value={ARCH_OPTIONS.find((a) => a.id === derived.arches)?.label || "—"}
                            hint="from plan"
                            testid="mfg-spec-arches"
                        />
                        <ReadOnlyField
                            label="Sheets / stage"
                            value={derived.sheets_per_stage || "—"}
                            hint={derived.arches === "both" ? "2 (Upper + Lower)" : derived.arches ? "1 (single arch)" : ""}
                            testid="mfg-spec-sheets-per-stage"
                        />
                        <div>
                            <Label>Retainers</Label>
                            <Input
                                type="number" min="0" max="10"
                                value={form.retainer_count}
                                onChange={(e) => upd("retainer_count", e.target.value)}
                                data-testid="mfg-spec-retainers"
                            />
                        </div>
                    </div>

                    {stagesOverflow && (
                        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 space-y-2.5" data-testid="mfg-spec-tier-overage">
                            <div className="flex items-start gap-2.5">
                                <Warning size={18} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                                <div className="text-sm text-amber-900 leading-relaxed">
                                    <span className="font-bold">Tier overage detected.</span>{" "}
                                    This case is on <span className="font-semibold">{tierLimit.tier_name}</span> which includes{" "}
                                    <span className="font-semibold">{tierLimit.included_aligners} aligners</span>.
                                    The current plan has <span className="font-semibold">{derived.aligner_stages_count} aligners</span>,
                                    exceeding the limit by <span className="font-semibold">{overageCount}</span>.
                                    {tierLimit.overage_per_aligner > 0 && (
                                        <> Estimated additional billing: <span className="font-semibold">₹{overageAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span> ({overageCount} × ₹{tierLimit.overage_per_aligner.toLocaleString("en-IN", { maximumFractionDigits: 0 })}/aligner).</>
                                    )}
                                </div>
                            </div>
                            <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer pl-7">
                                <input
                                    type="checkbox"
                                    checked={tierOverageAck}
                                    onChange={(e) => setTierOverageAck(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="mfg-spec-tier-overage-ack"
                                />
                                <span>
                                    I confirm the overage. Admin will be notified and the invoice will be flagged for additional billing.
                                </span>
                            </label>
                        </div>
                    )}
                    {tierLimit && !stagesOverflow && derived.aligner_stages_count > 0 && (
                        <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
                            <CheckCircle size={12} weight="fill" /> Within tier — {derived.aligner_stages_count} of {tierLimit.included_aligners} stages on {tierLimit.tier_name}.
                        </div>
                    )}
                    {!derived.ready && (
                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 flex items-center gap-1.5" data-testid="mfg-spec-plan-empty">
                            <Warning size={14} weight="fill" /> Set Upper / Lower aligners in the <span className="font-semibold">Aligners &amp; Wear Schedule</span> section above — Manufacturing Specs auto-fills from there.
                        </div>
                    )}
                    <div>
                        <Label>Notes for production (optional)</Label>
                        <Textarea
                            value={form.notes}
                            onChange={(e) => upd("notes", e.target.value)}
                            rows={2}
                            placeholder="e.g. Use slow thermoform on first 3 stages, IPR refinement at stage 12."
                            data-testid="mfg-spec-notes"
                        />
                    </div>
                    <PhaseEditor
                        phases={form.phases || []}
                        totalStages={Number(form.aligner_stages_count) || 0}
                        onChange={(phases) => updEager("phases", phases)}
                    />
                    {/* iter98.99da — Manufacturing summary now spells out
                        stages → physical pieces so the lab gets the
                        clinically-correct unit (stages) AND the inventory
                        unit (sheets) at a glance. */}
                    <div className="text-xs text-muted-foreground bg-secondary/40 p-2 rounded-lg space-y-0.5" data-testid="mfg-spec-summary">
                        <div>
                            Treatment progression: <span className="font-bold text-foreground">{Number(form.aligner_stages_count) || 0}</span> stages
                            {(Number(derived.aligner_count_upper) > 0 || Number(derived.aligner_count_lower) > 0) && (
                                <> · <span className="font-mono">{Number(derived.aligner_count_upper) || 0} U + {Number(derived.aligner_count_lower) || 0} L = {(Number(derived.aligner_count_upper) || 0) + (Number(derived.aligner_count_lower) || 0)} aligners total</span></>
                            )}
                        </div>
                        <div>
                            Production will need <span className="font-bold text-foreground">{(Number(form.aligner_stages_count) || 0) * (Number(form.sheets_per_stage) || 1)}</span> aligner sheets
                            {Number(form.retainer_count) > 0 && <> + <span className="font-bold text-foreground">{form.retainer_count}</span> retainer sheets</>}
                        </div>
                    </div>
                </div>
            </PlanSectionAccordion>
        </div>
    );
}


/* iter97.43 — Save-status indicator displayed in the accordion header.
   Mirrors PlanEditorInline's SaveIndicator so both surfaces use the
   same visual language (saving / saved at HH:MM:SS / error). */
function SpecSaveIndicator({ state }) {
    if (state.status === "saving") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="mfg-spec-save-state">
                <CircleNotch size={12} className="animate-spin" /> Saving…
            </span>
        );
    }
    if (state.status === "saved" && state.at) {
        const time = state.at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="mfg-spec-save-state">
                <CheckCircle size={12} weight="fill" /> Saved {time}
            </span>
        );
    }
    if (state.status === "error") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-rose-700" data-testid="mfg-spec-save-state" title={state.error || ""}>
                <WarningCircle size={12} weight="fill" /> Couldn&apos;t save
            </span>
        );
    }
    return null;
}


function SpecDisplay({ spec, derived, locked, totalSheets }) {
    if (!spec.aligner_sheet_material_id) {
        /* iter97.42 — Even before the spec is saved, preview the live derived
           stages from the plan so the planner sees what production WILL get
           once they click "Set spec". Gives the panel a useful read-out even
           in its empty state. */
        if (!locked && derived && derived.ready) {
            const archLabel = ARCH_OPTIONS.find((x) => x.id === derived.arches)?.label || "Upper + Lower";
            const sheets = (Number(derived.aligner_stages_count) || 0) * (Number(derived.sheets_per_stage) || 1);
            return (
                <div className="space-y-2.5" data-testid="mfg-spec-empty-preview">
                    <div className="text-sm text-muted-foreground italic">No spec set yet — pick the aligner sheet to lock production in.</div>
                    <div className="grid grid-cols-4 gap-3">
                        <SpecField label="Stages">
                            {derived.aligner_stages_count}
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-emerald-700">Live</span>
                        </SpecField>
                        <SpecField label="Arches">{archLabel}</SpecField>
                        <SpecField label="Sheets / stage">{derived.sheets_per_stage}</SpecField>
                        <SpecField label="Retainers"><span className="text-muted-foreground italic">tbd</span></SpecField>
                    </div>
                    <div className="text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg p-2.5">
                        Plan currently needs <span className="font-bold text-foreground">{sheets}</span> aligner sheet{sheets === 1 ? "" : "s"}. Click &ldquo;Set spec&rdquo; above to confirm which TPU.
                    </div>
                </div>
            );
        }
        return <div className="text-sm text-muted-foreground italic py-2">No spec set yet.</div>;
    }
    const a = spec.aligner_sheet_material;
    /* iter97.42 — When the case is not yet locked into production, show the
       LIVE derived stages/arches/sheets from the current plan instead of
       the saved snapshot. This makes the panel a true "calculated output"
       that tracks the planner's edits in real time. Once locked, we
       freeze to the snapshot for production accountability. */
    const showLive = !locked && derived && derived.ready;
    const stagesLive = showLive ? derived.aligner_stages_count : spec.aligner_stages_count;
    const archesLive = showLive ? derived.arches : spec.arches;
    const sheetsPerStageLive = showLive ? derived.sheets_per_stage : spec.sheets_per_stage;
    const totalSheetsLive = showLive
        ? (Number(derived.aligner_stages_count) || 0) * (Number(derived.sheets_per_stage) || 1)
        : totalSheets;
    const archLabel = ARCH_OPTIONS.find((x) => x.id === archesLive)?.label || "Upper + Lower";
    /* Snapshot drift: planner saved spec when plan was X aligners, then
       edited plan to Y. Surface a gentle "re-save to lock" hint so they
       know the saved spec doesn't match the current plan. */
    const driftStages = showLive
        && Number(spec.aligner_stages_count) > 0
        && Number(spec.aligner_stages_count) !== Number(derived.aligner_stages_count);
    return (
        <div className="space-y-3" data-testid="mfg-spec-display">
            <div className="grid grid-cols-2 gap-3">
                <SpecField label="Aligner sheet">
                    {a ? (
                        <>
                            <span className="font-mono text-[10px] text-muted-foreground mr-1.5">{a.sku}</span>
                            <span className="font-medium">{a.name}</span>
                            <span className="text-[10px] text-muted-foreground ml-2">stock: {a.current_stock} {a.unit}</span>
                        </>
                    ) : <span className="text-muted-foreground">—</span>}
                </SpecField>
                {/* iter97.42 — retainer sheet is always "Invisique Infinite"
                    (brand-standard). Static badge replaces the lookup. */}
                <SpecField label="Retainer sheet">
                    <span className="inline-flex items-center gap-1.5">
                        <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0" />
                        <span className="font-medium">Invisique™ Infinite</span>
                        <span className="text-[10px] text-muted-foreground">brand-standard</span>
                    </span>
                </SpecField>
            </div>
            <div className="grid grid-cols-4 gap-3">
                <SpecField label="Stages">
                    {stagesLive || "—"}
                    {showLive && <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-emerald-700">Live</span>}
                </SpecField>
                <SpecField label="Arches">{archLabel}</SpecField>
                <SpecField label="Sheets / stage">{sheetsPerStageLive || "—"}</SpecField>
                <SpecField label="Retainers">{spec.retainer_count}</SpecField>
            </div>
            {driftStages && (
                <div
                    className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5"
                    data-testid="mfg-spec-drift-notice"
                >
                    <Warning size={12} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                    <span>
                        Snapshot is <span className="font-semibold">{spec.aligner_stages_count}</span> but the plan now shows <span className="font-semibold">{derived.aligner_stages_count}</span>. The live values above will be locked when you re-save the spec.
                    </span>
                </div>
            )}
            <div className="text-xs bg-primary/5 border border-primary/20 rounded-lg p-2.5">
                <span className="text-muted-foreground">Production will use </span>
                <span className="font-bold text-foreground">{totalSheetsLive}</span>
                <span className="text-muted-foreground"> aligner sheet{totalSheetsLive === 1 ? "" : "s"}</span>
                {spec.retainer_count > 0 && <> <span className="text-muted-foreground">+ </span><span className="font-bold text-foreground">{spec.retainer_count}</span><span className="text-muted-foreground"> retainer sheet{spec.retainer_count === 1 ? "" : "s"}</span></>}
            </div>
            {spec.notes && (
                <div className="text-xs text-muted-foreground bg-secondary/30 p-2.5 rounded-lg italic">&ldquo;{spec.notes}&rdquo;</div>
            )}
            {/* iter82 — phases shown when planner explicitly added them.
                Hidden for default single-shipment cases. */}
            <PhasesSummary phases={spec.phases} />
            {spec.set_by_name && (
                <div className="text-[10px] text-muted-foreground">
                    Set by {spec.set_by_name} on {new Date(spec.set_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
            )}
        </div>
    );
}


/* iter97.42 — Static "Invisique Infinite" pill shown in the edit form
   in place of the retainer sheet dropdown. Matches the plan retention
   pill style so the planner gets the same visual language across the
   case workspace. Saves no state — just communicates the default. */
function RetainerStaticPill() {
    return (
        <div>
            <Label>Retainer sheet</Label>
            <div
                className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-3 py-2.5 flex items-center gap-2.5"
                data-testid="mfg-spec-retainer-static"
            >
                <div className="w-7 h-7 rounded-full bg-emerald-500 grid place-items-center text-white shrink-0">
                    <CheckCircle size={14} weight="fill" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900 leading-tight">Invisique™ Infinite</div>
                    <p className="text-[10px] text-emerald-900/80 mt-0.5">
                        Brand-standard retention bundle. Lifetime replacement under the Invisique™ Infinite program.
                    </p>
                </div>
            </div>
        </div>
    );
}


function SpecField({ label, children }) {
    return (
        <div className="bg-secondary/30 rounded-lg p-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">{label}</div>
            <div className="text-sm">{children}</div>
        </div>
    );
}


/* iter97.35 — Read-only field used in the edit form for values that are
   derived from the plan (Stages, Arches, Sheets/stage). Visually similar
   to an input box so the planner sees the value clearly, but the styling
   communicates "this is calculated, you can't edit it here". */
function ReadOnlyField({ label, value, hint, testid }) {
    return (
        <div>
            <Label className="flex items-center gap-1">
                {label}
                <Lock size={9} weight="fill" className="text-muted-foreground opacity-60" />
            </Label>
            <div
                className="h-10 px-3 rounded-md border border-input bg-secondary/40 text-sm flex items-center text-foreground font-semibold tabular-nums"
                data-testid={testid}
            >
                {value}
            </div>
            {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate" title={hint}>{hint}</div>}
        </div>
    );
}



/* ============================================================================
   PhaseEditor — planner authors how the fabrication will be physically
   dispatched. Default = empty (single-shipment). When the planner adds
   2+ phases, the production team will create one shipment per phase at
   dispatch time. Doctor sees the phase plan upfront on the Lab &
   Production card so they know in advance how many packages to expect.

   UX guarantees:
     - "Single shipment" mode shows a clear one-liner explaining the default.
     - "Add a phase" appends a sensible split — first add splits in half.
     - Phase rows are sequential; can't add a row 2 if row 1 covers all stages.
     - Last row's stage_to is locked to total stages so the user can't
       accidentally leave a gap at the end.
   ========================================================================== */

function PhaseEditor({ phases, totalStages, onChange }) {
    const hasPhases = phases.length > 0;
    const disabled = totalStages < 2;

    const addFirstPhase = () => {
        // Split in half as a useful starting point. Planner can adjust
        // boundaries inline before saving.
        const split = Math.floor(totalStages / 2);
        onChange([
            { stage_from: 1, stage_to: split },
            { stage_from: split + 1, stage_to: totalStages },
        ]);
    };

    const addPhase = () => {
        // New phase starts after the last one's stage_to + 1 if there's
        // room. Otherwise no-op (the button is disabled in that case).
        if (phases.length === 0) { addFirstPhase(); return; }
        const sorted = [...phases].sort((a, b) => Number(a.stage_from) - Number(b.stage_from));
        const lastTo = Number(sorted[sorted.length - 1].stage_to);
        if (lastTo >= totalStages) return;
        // Shrink the previous last phase's stage_to to make room for a new one.
        const prevTo = Math.max(lastTo - 1, Number(sorted[sorted.length - 1].stage_from));
        sorted[sorted.length - 1] = { ...sorted[sorted.length - 1], stage_to: prevTo };
        sorted.push({ stage_from: prevTo + 1, stage_to: totalStages });
        onChange(sorted);
    };

    const removePhase = (idx) => {
        // Removing all but one phase collapses back to single-shipment.
        if (phases.length <= 2) { onChange([]); return; }
        const next = phases.filter((_, i) => i !== idx);
        // Extend the previous phase to cover the removed range, keeps contiguity.
        const sorted = [...next].sort((a, b) => Number(a.stage_from) - Number(b.stage_from));
        for (let i = 1; i < sorted.length; i++) {
            sorted[i] = { ...sorted[i], stage_from: Number(sorted[i - 1].stage_to) + 1 };
        }
        sorted[sorted.length - 1] = { ...sorted[sorted.length - 1], stage_to: totalStages };
        onChange(sorted);
    };

    const updatePhase = (idx, field, value) => {
        const next = [...phases];
        next[idx] = { ...next[idx], [field]: value };
        onChange(next);
    };

    if (disabled) return null;

    return (
        <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-3" data-testid="phase-editor">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-start gap-2 min-w-0">
                    <Truck size={14} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                            Dispatch phasing {hasPhases && <span className="text-primary">· {phases.length} phases</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            {hasPhases
                                ? "Production will dispatch each phase separately. The doctor sees this plan upfront."
                                : "Single shipment — all aligners dispatched together. Add phases to split for revision-risk cases."}
                        </div>
                    </div>
                </div>
                {!hasPhases && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addFirstPhase}
                        className="rounded-full shrink-0"
                        data-testid="phase-editor-add-first"
                    >
                        <Plus size={12} weight="bold" className="mr-1" /> Split into phases
                    </Button>
                )}
            </div>

            {hasPhases && (
                <div className="space-y-2">
                    {phases.map((p, idx) => {
                        const isLast = idx === phases.length - 1;
                        const isFirst = idx === 0;
                        const stageCount = (Number(p.stage_to) - Number(p.stage_from) + 1) || 0;
                        return (
                            <div
                                key={idx}
                                className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm"
                                data-testid={`phase-editor-row-${idx}`}
                            >
                                <span className="font-bold text-xs uppercase tracking-wider text-muted-foreground w-16 shrink-0">
                                    Phase {idx + 1}
                                </span>
                                <div className="flex items-center gap-1 flex-1">
                                    <span className="text-xs text-muted-foreground">Stages</span>
                                    <Input
                                        type="number"
                                        min={isFirst ? 1 : (Number(phases[idx - 1]?.stage_to || 0) + 1)}
                                        max={Number(p.stage_to)}
                                        value={p.stage_from}
                                        onChange={(e) => updatePhase(idx, "stage_from", Number(e.target.value))}
                                        // First phase ALWAYS starts at 1; lock it
                                        // visually so the planner doesn't even try.
                                        disabled={isFirst}
                                        className="h-8 w-16 text-sm"
                                        data-testid={`phase-editor-row-${idx}-from`}
                                    />
                                    <span className="text-xs text-muted-foreground">to</span>
                                    <Input
                                        type="number"
                                        min={Number(p.stage_from)}
                                        max={isLast ? totalStages : Number(phases[idx + 1]?.stage_from || totalStages) - 1}
                                        value={p.stage_to}
                                        onChange={(e) => {
                                            const newTo = Number(e.target.value);
                                            updatePhase(idx, "stage_to", newTo);
                                            // Cascade: bump the next phase's stage_from to keep contiguity.
                                            if (!isLast) {
                                                const next = [...phases];
                                                next[idx] = { ...next[idx], stage_to: newTo };
                                                next[idx + 1] = { ...next[idx + 1], stage_from: newTo + 1 };
                                                onChange(next);
                                            }
                                        }}
                                        // Last phase ALWAYS ends at total stages; lock visually.
                                        disabled={isLast}
                                        className="h-8 w-16 text-sm"
                                        data-testid={`phase-editor-row-${idx}-to`}
                                    />
                                    <span className="text-[11px] text-muted-foreground ml-2">
                                        {stageCount} {stageCount === 1 ? "stage" : "stages"}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removePhase(idx)}
                                    className="p-1 text-muted-foreground hover:text-rose-700 transition shrink-0"
                                    title="Remove this phase (merges into adjacent phase)"
                                    data-testid={`phase-editor-row-${idx}-remove`}
                                >
                                    <Trash size={14} />
                                </button>
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        onClick={addPhase}
                        className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                        data-testid="phase-editor-add-more"
                        disabled={phases[phases.length - 1] && Number(phases[phases.length - 1].stage_to) - Number(phases[phases.length - 1].stage_from) < 1}
                    >
                        <Plus size={12} weight="bold" /> Add another phase
                    </button>
                </div>
            )}
        </div>
    );
}


/* ============================================================================
   PhasesSummary — read-only display of phases on the locked spec view.
   Hidden when there are no phases (default single-shipment case).
   ========================================================================== */

export function PhasesSummary({ phases }) {
    if (!phases || phases.length === 0) return null;
    return (
        <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                <Truck size={11} weight="duotone" /> Dispatch phasing · {phases.length} phases
            </div>
            <div className="flex flex-wrap gap-1.5">
                {phases.map((p, idx) => (
                    <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-border text-[11px] font-semibold"
                        data-testid={`phases-summary-${idx}`}
                    >
                        Phase {idx + 1}: stages {p.stage_from}–{p.stage_to}
                    </span>
                ))}
            </div>
        </div>
    );
}
